import React from 'react';
import classnames from 'clsx';
import { v4 as generateId } from 'uuid';

import { HAS_CUSTOM_FOCUS_CLASSNAME } from '@wix/editor-elements-common-utils';

import { platformIds } from '../constants';
import type { CopyButtonWrapperProps } from '../types';
import { copyToClipboard } from '../utils/clipBoard';
import { classes, st } from './style/ShareButton.component.st.css';
import { classes as shareButtonsClasses } from './style/ShareButtons.component.st.css';
import { Tooltip } from '../../../common/tooltip/Tooltip';
import Checkmark from '../assets/checkmark.svg';

const TOOLTIP_HIDE_TIMEOUT = 5000;

const IconComponent: React.FC = () => {
  return (
    <span className={st(classes.root, shareButtonsClasses.button)}>
      <span className={st(classes.icon)}>
        <Checkmark />
      </span>
    </span>
  );
};

const CopyButtonWrapper: React.FC<CopyButtonWrapperProps> = ({
  id,
  children,
  url,
  ariaLabel,
  onCopyButtonClicked,
  linkCopiedText,
}) => {
  const [showTooltip, setShowTooltip] = React.useState(false);
  const tooltipId = generateId();

  const _onClick = async () => {
    await copyToClipboard(decodeURI(url));
    onCopyButtonClicked();
    setShowTooltip(true);
  };

  React.useEffect(() => {
    let hideTooltipTimeout: NodeJS.Timeout;

    if (showTooltip) {
      hideTooltipTimeout = setTimeout(() => {
        setShowTooltip(false);
      }, TOOLTIP_HIDE_TIMEOUT);
    }

    return () => {
      clearTimeout(hideTooltipTimeout);
    };
  }, [showTooltip]);

  const classname = classnames(st(shareButtonsClasses.container), {
    [st(classes.showTooltip)]: showTooltip,
  });

  return (
    <div className={classname}>
      {showTooltip && (
        <Tooltip
          tooltipId={tooltipId}
          dataTestId="copy-link-tooltip"
          iconComponent={IconComponent}
        >
          {linkCopiedText}
        </Tooltip>
      )}
      <div className={st(classes.copyButtonWrapper)}>
        <button
          id={id}
          className={st(
            classes.root,
            shareButtonsClasses.button,
            HAS_CUSTOM_FOCUS_CLASSNAME,
          )}
          data-testid={platformIds.copyButton}
          onClick={_onClick}
          aria-label={ariaLabel}
          aria-live="assertive"
        >
          {children}
        </button>
      </div>
    </div>
  );
};

export default CopyButtonWrapper;
